/**
 * QuestionForm - Structured question form for ask_user_tool
 *
 * Question types:
 * - ChoiceInput: Radio buttons, "Other" shows only when selected, inline note
 * - MultiChoiceInput: Checkboxes with arrow key navigation, "Other" on select
 * - BooleanInput: Yes/No toggle buttons
 * - NumericInput: Slider + input, input only, or both (configurable via input_mode)
 * - RangeInput: Dual slider + inputs, inputs only, or both
 * - TextInput: Freeform text (last-resort; agent should usually use choice)
 *
 * After submission, displays inline AnswerSummary with edit capability.
 * Tab moves between questions. Arrow keys navigate within choice options.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Pencil } from 'lucide-react';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import { useNodes, useNode } from '@sourcepilot/framework/relationship-manager';
import { useEditable } from '@sourcepilot/ui/framework/data/hooks/use-editable';
import { Answer } from '@sourcepilot/generated/tasks/api/answer';

interface QuestionFormProps {
  questionnaire: any;
  readOnly?: boolean;
}

export function QuestionForm({ questionnaire, readOnly = false }: QuestionFormProps) {
  const editable = useEditable(questionnaire);
  const questions = useNodes(questionnaire?.questions)?.orderBy('order_index').value() || [];
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Initialize defaults from question default_value fields
  useEffect(() => {
    const defaults: Record<string, any> = {};
    for (const q of questions as any[]) {
      const key = q.key || '0';
      if (q.default_value != null && answers[key] === undefined) {
        if (q.type === 'choice') {
          defaults[key] = { value: q.default_value, is_other: false, note: null };
        } else if (q.type === 'multi_choice') {
          const vals = Array.isArray(q.default_value) ? q.default_value : [q.default_value];
          defaults[key] = vals.map((v: any) => ({ value: v, is_other: false, note: null }));
        } else {
          defaults[key] = q.default_value;
        }
      }
    }
    if (Object.keys(defaults).length > 0) {
      setAnswers(prev => ({ ...defaults, ...prev }));
    }
  }, [questions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAnswer = (key: string, value: any) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const focusNextQuestion = (currentKey: string) => {
    const keys = questions.map((q: any) => q.key || '0');
    const idx = keys.indexOf(currentKey);
    if (idx >= 0 && idx < keys.length - 1) {
      const nextRef = questionRefs.current[keys[idx + 1]];
      const focusable = nextRef?.querySelector('input, button, select') as HTMLElement;
      focusable?.focus();
    }
  };

  const handleSubmit = async () => {
    if (!editable) return;
    setIsSubmitting(true);
    setError(null);
    try {
      for (const q of (editable as any).questions) {
        let raw = answers[q.key || '0'];
        if (raw === undefined) {
          if (q.type === 'numeric') raw = q.default_value ?? q.min_value ?? 0;
          else if (q.type === 'range') raw = q.default_value ?? { min: q.min_value ?? 0, max: q.max_value ?? 100 };
        }
        q.answer.set(new Answer({ value: raw ?? null }));
      }
      await (editable as any).save();
    } catch (err: any) {
      console.error('Failed to submit answers:', err);
      setError(err.message || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (questions.length === 0) {
    return (
      <div className="mt-2 text-xs text-gray-500 animate-pulse">
        Loading questions...
      </div>
    );
  }

  return (
    <div className="mt-2 border border-blue-200 rounded-lg p-3 bg-blue-50">
      <div className="space-y-4">
        {questions.map((q: any) => (
          <div
            key={q.key || q.id}
            ref={el => { questionRefs.current[q.key || '0'] = el; }}
          >
            <QuestionItem
              question={q}
              pendingValue={answers[q.key || '0']}
              onChange={(val) => updateAnswer(q.key || '0', val)}
              onTab={() => focusNextQuestion(q.key || '0')}
              readOnly={readOnly}
            />
          </div>
        ))}
      </div>
      {!readOnly && (
        <>
          {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
          <div className="mt-3">
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
              Submit
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================================
// QuestionItem — dispatches between input and answer summary
// ============================================================================

function QuestionItem({ question, pendingValue, onChange, onTab, readOnly }: {
  question: any;
  pendingValue: any;
  onChange: (val: any) => void;
  onTab: () => void;
  readOnly: boolean;
}) {
  const existingAnswer = useNode(question.answer) as any;
  const isAnswered = !!existingAnswer;
  const [isEditing, setIsEditing] = useState(false);

  if (isAnswered && !isEditing) {
    return (
      <AnswerSummary
        question={question}
        answer={existingAnswer}
        onEdit={readOnly ? undefined : () => setIsEditing(true)}
      />
    );
  }

  return (
    <QuestionInput
      question={question}
      value={isEditing ? existingAnswer?.value : pendingValue}
      onChange={onChange}
      onTab={onTab}
      disabled={readOnly}
    />
  );
}


// ============================================================================
// AnswerSummary — inline read-only display
// ============================================================================

function AnswerSummary({ question, answer, onEdit }: {
  question: any;
  answer: any;
  onEdit?: () => void;
}) {
  const type = question.type || 'choice';
  const value = answer.value;
  const options: Array<{label: string; value: any}> = Array.isArray(question.options) ? question.options : [];
  const findLabel = (v: any) => options.find(o => o.value === v)?.label ?? String(v);

  let summary: React.ReactNode;

  if (type === 'boolean') {
    summary = value === true ? 'Yes' : value === false ? 'No' : '—';
  } else if (type === 'numeric') {
    summary = value != null ? `${value}${question.unit ? ` ${question.unit}` : ''}` : '—';
  } else if (type === 'range' && value && typeof value === 'object') {
    const u = question.unit ? ` ${question.unit}` : '';
    summary = value.min === value.max
      ? `${value.min}${u}`
      : `${value.min} — ${value.max}${u}`;
  } else if (type === 'choice' && value && typeof value === 'object') {
    const label = value.is_other ? `Other: "${value.value}"` : findLabel(value.value);
    summary = (
      <span>
        {label}
        {value.note && <span className="text-gray-500 ml-1 text-xs italic">({value.note})</span>}
      </span>
    );
  } else if (type === 'multi_choice' && Array.isArray(value)) {
    const items = value.map((v: any, i: number) => {
      const label = v.is_other ? `Other: "${v.value}"` : findLabel(v.value);
      return (
        <span key={i}>
          {label}
          {v.note && <span className="text-gray-500 text-xs italic"> ({v.note})</span>}
        </span>
      );
    });
    summary = items.length > 0 ? <span>{items.reduce((a: any, b: any) => [a, ', ', b] as any)}</span> : '—';
  } else if (type === 'text') {
    summary = value ? String(value) : '—';
  } else {
    summary = String(value ?? '—');
  }

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="text-sm">
        <span className="font-medium text-gray-700">{question.question_text}: </span>
        <span className="text-gray-900">{summary}</span>
      </div>
      {onEdit && (
        <button onClick={onEdit} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0" title="Edit answer">
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}


// ============================================================================
// QuestionInput — type-specific input dispatcher
// ============================================================================

function QuestionInput({ question, value, onChange, onTab, disabled }: {
  question: any;
  value: any;
  onChange: (val: any) => void;
  onTab: () => void;
  disabled: boolean;
}) {
  const type = question.type || 'choice';
  const options: Array<{label: string; value: any}> = Array.isArray(question.options) ? question.options : [];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {question.question_text}
        {question.unit && <span className="text-gray-400 font-normal ml-1">({question.unit})</span>}
      </label>

      {type === 'choice' && (
        <ChoiceInput options={options} allowOther={question.allow_other ?? true}
          value={value} onChange={onChange} onTab={onTab} disabled={disabled} />
      )}
      {type === 'multi_choice' && (
        <MultiChoiceInput options={options} allowOther={question.allow_other ?? true}
          value={value} onChange={onChange} onTab={onTab} disabled={disabled} />
      )}
      {type === 'boolean' && (
        <BooleanInput value={value} onChange={onChange} onTab={onTab} disabled={disabled} />
      )}
      {type === 'numeric' && (
        <NumericInput value={value} onChange={onChange} onTab={onTab}
          min={question.min_value} max={question.max_value}
          step={question.step} unit={question.unit}
          inputMode={question.input_mode} disabled={disabled} />
      )}
      {type === 'range' && (
        <RangeInput value={value} onChange={onChange} onTab={onTab}
          min={question.min_value ?? 0} max={question.max_value ?? 100}
          step={question.step} unit={question.unit}
          inputMode={question.input_mode} disabled={disabled} />
      )}
      {type === 'text' && (
        <TextInput value={value} onChange={onChange} onTab={onTab} disabled={disabled} />
      )}
    </div>
  );
}


// ============================================================================
// TextInput — freeform single-line text (last-resort question type)
// ============================================================================

function TextInput({ value, onChange, onTab, disabled }: {
  value: any;
  onChange: (val: string) => void;
  onTab: () => void;
  disabled: boolean;
}) {
  return (
    <input
      type="text"
      className="w-full max-w-lg px-2 py-0.5 text-sm border border-gray-300 rounded bg-white"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder="Type your answer..."
      onKeyDown={(e) => {
        if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); }
      }}
      autoFocus
    />
  );
}


// ============================================================================
// ChoiceInput — radio buttons, inline note on selected, Other on select only
// ============================================================================

function ChoiceInput({ options, allowOther, value, onChange, onTab, disabled }: {
  options: Array<{label: string; value: any}>;
  allowOther: boolean;
  value: any;
  onChange: (val: any) => void;
  onTab: () => void;
  disabled: boolean;
}) {
  const selected = value?.value;
  const isOther = value?.is_other ?? false;
  const note = value?.note ?? '';
  const [otherText, setOtherText] = useState(isOther ? String(selected || '') : '');
  const [noteText, setNoteText] = useState(note);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectOption = (optValue: any) => {
    onChange({ value: optValue, is_other: false, note: noteText || null });
  };

  const selectOther = (text: string) => {
    setOtherText(text);
    onChange({ value: text, is_other: true, note: null });
  };

  const updateNote = (text: string) => {
    setNoteText(text);
    if (value) onChange({ ...value, note: text || null });
  };

  // Arrow key navigation between radio options
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      // Only intercept tab if not in a text input
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' || (target as HTMLInputElement).type === 'radio') {
        e.preventDefault();
        onTab();
      }
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const radios = containerRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      if (!radios) return;
      const arr = Array.from(radios);
      const idx = arr.findIndex(r => r === document.activeElement);
      const next = e.key === 'ArrowDown' ? (idx + 1) % arr.length : (idx - 1 + arr.length) % arr.length;
      arr[next].focus();
      arr[next].click();
    }
  };

  return (
    <div className="space-y-0.5" ref={containerRef} onKeyDown={handleKeyDown}>
      {options.map((opt) => {
        const isSelected = !isOther && selected === opt.value;
        return (
          <div key={String(opt.value)} className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer shrink-0">
              <input type="radio" checked={isSelected} onChange={() => selectOption(opt.value)}
                disabled={disabled} className="text-blue-600" />
              {opt.label}
            </label>
            {/* Inline note — same line as selected option */}
            {isSelected && (
              <input type="text"
                className="flex-1 max-w-xs px-2 py-0 text-xs border border-gray-200 rounded bg-white text-gray-500 h-6"
                value={noteText} onChange={(e) => updateNote(e.target.value)}
                placeholder="add a note" disabled={disabled}
                onKeyDown={(e) => { if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); } }}
              />
            )}
          </div>
        );
      })}
      {allowOther && (
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer shrink-0">
            <input type="radio" checked={isOther} onChange={() => selectOther(otherText)}
              disabled={disabled} className="text-blue-600" />
            Other
          </label>
          {/* Other text box only shows when Other is selected */}
          {isOther && (
            <input type="text"
              className="flex-1 max-w-sm px-2 py-0.5 text-sm border border-gray-300 rounded bg-white"
              value={otherText} onChange={(e) => selectOther(e.target.value)}
              placeholder="Type your answer..." disabled={disabled} autoFocus
              onKeyDown={(e) => { if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); } }}
            />
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// MultiChoiceInput — checkboxes with arrow key nav
// ============================================================================

function MultiChoiceInput({ options, allowOther, value, onChange, onTab, disabled }: {
  options: Array<{label: string; value: any}>;
  allowOther: boolean;
  value: any;
  onChange: (val: any) => void;
  onTab: () => void;
  disabled: boolean;
}) {
  const selections: Array<{value: any; is_other: boolean; note: string | null}> = Array.isArray(value) ? value : [];
  const [otherText, setOtherText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const isSelected = (optValue: any) => selections.some(s => !s.is_other && s.value === optValue);
  const hasOther = selections.some(s => s.is_other);

  const toggleOption = (optValue: any) => {
    if (isSelected(optValue)) {
      onChange(selections.filter(s => !(s.value === optValue && !s.is_other)));
    } else {
      onChange([...selections, { value: optValue, is_other: false, note: null }]);
    }
  };

  const toggleOther = () => {
    if (hasOther) {
      onChange(selections.filter(s => !s.is_other));
    } else {
      onChange([...selections, { value: otherText, is_other: true, note: null }]);
    }
  };

  const updateOtherText = (text: string) => {
    setOtherText(text);
    onChange(selections.map(s => s.is_other ? { ...s, value: text } : s));
  };

  // Arrow key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' || (target as HTMLInputElement).type === 'checkbox') {
        e.preventDefault();
        onTab();
      }
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const checks = containerRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
      if (!checks) return;
      const arr = Array.from(checks);
      const idx = arr.findIndex(r => r === document.activeElement);
      const next = e.key === 'ArrowDown' ? (idx + 1) % arr.length : (idx - 1 + arr.length) % arr.length;
      arr[next].focus();
    }
  };

  return (
    <div className="space-y-0.5" ref={containerRef} onKeyDown={handleKeyDown}>
      {options.map((opt) => (
        <label key={String(opt.value)} className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isSelected(opt.value)}
            onChange={() => toggleOption(opt.value)} disabled={disabled} />
          {opt.label}
        </label>
      ))}
      {allowOther && (
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer shrink-0">
            <input type="checkbox" checked={hasOther} onChange={toggleOther} disabled={disabled} />
            Other
          </label>
          {hasOther && (
            <input type="text"
              className="flex-1 max-w-sm px-2 py-0.5 text-sm border border-gray-300 rounded bg-white"
              value={otherText} onChange={(e) => updateOtherText(e.target.value)}
              placeholder="Type your answer..." disabled={disabled} autoFocus
              onKeyDown={(e) => { if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); } }}
            />
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// BooleanInput — Yes/No toggle buttons
// ============================================================================

function BooleanInput({ value, onChange, onTab, disabled }: {
  value: any;
  onChange: (val: boolean) => void;
  onTab: () => void;
  disabled: boolean;
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onChange(true); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onChange(false); }
  };

  return (
    <div className="flex gap-2" role="radiogroup" onKeyDown={handleKeyDown}>
      <button type="button" disabled={disabled} role="radio" aria-checked={value === true}
        className={`px-4 py-1 text-sm rounded border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${
          value === true ? 'bg-blue-100 border-blue-400 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
        onClick={() => onChange(true)}>
        Yes
      </button>
      <button type="button" disabled={disabled} role="radio" aria-checked={value === false}
        className={`px-4 py-1 text-sm rounded border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${
          value === false ? 'bg-blue-100 border-blue-400 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
        onClick={() => onChange(false)}>
        No
      </button>
    </div>
  );
}


// ============================================================================
// Shared: ClampedNumberInput — free typing, clamp on blur
// ============================================================================

function ClampedNumberInput({ value, onChange, min, max, step, disabled, className }: {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled: boolean;
  className?: string;
}) {
  // Determine decimal precision from step (e.g. step=0.5 → 1 decimal place)
  const precision = step ? Math.max(0, -Math.floor(Math.log10(step))) : 0;
  const fmt = (v: number) => precision > 0 ? v.toFixed(precision) : String(v);

  const [localValue, setLocalValue] = useState(fmt(value));
  const isFocused = useRef(false);

  // Sync from parent when not focused
  useEffect(() => {
    if (!isFocused.current) setLocalValue(fmt(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input type="number"
      className={className ?? "w-20 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-center"}
      value={localValue}
      step={step}
      min={min}
      max={max}
      onChange={(e) => {
        setLocalValue(e.target.value);
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(v);
      }}
      onFocus={() => { isFocused.current = true; }}
      onBlur={() => {
        isFocused.current = false;
        let v = parseFloat(localValue);
        if (isNaN(v)) v = min ?? 0;
        if (min != null && v < min) v = min;
        if (max != null && v > max) v = max;
        setLocalValue(fmt(v));
        onChange(v);
      }}
      disabled={disabled}
    />
  );
}

// Shared slider thumb styles
const SLIDER_THUMB = `
  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500
  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
  [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer
  [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500
  [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white
  [&::-moz-range-thumb]:shadow [&::-moz-range-thumb]:cursor-pointer
`.trim();

// ============================================================================
// SingleSlider — styled single-thumb slider with filled track
// ============================================================================

function SingleSlider({ value, min, max, step, onChange, disabled }: {
  value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; disabled: boolean;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative flex-1 h-6 flex items-center">
      <div className="absolute left-0 right-0 h-1 bg-gray-200 rounded" />
      <div className="absolute h-1 bg-blue-400 rounded" style={{ left: 0, width: `${pct}%` }} />
      <input type="range" tabIndex={-1}
        className={`absolute left-0 right-0 appearance-none bg-transparent ${SLIDER_THUMB}`}
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))} disabled={disabled} />
    </div>
  );
}

// ============================================================================
// NumericInput — slider + input, input only, or both
// ============================================================================

function NumericInput({ value, onChange, onTab, min, max, step, unit, inputMode, disabled }: {
  value: any;
  onChange: (val: number) => void;
  onTab: () => void;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  unit?: string | null;
  inputMode?: string | null;
  disabled: boolean;
}) {
  const hasRange = min != null && max != null;
  const mode = inputMode ?? (hasRange ? 'both' : 'input');
  const numValue = typeof value === 'number' ? value : (min ?? 0);
  const s = step ?? (hasRange ? (max! - min!) / 100 : 1);

  const showSlider = mode === 'slider' || mode === 'both';
  const showInput = mode === 'input' || mode === 'both';

  return (
    <div className="space-y-1 max-w-lg" onKeyDown={(e) => {
      if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); }
    }}>
      <div className="flex items-center gap-2">
        {showInput && (
          <ClampedNumberInput value={numValue} onChange={onChange}
            min={min ?? undefined} max={max ?? undefined} step={s}
            disabled={disabled}
            className="w-20 px-2 py-0.5 text-sm border border-gray-300 rounded bg-white text-center" />
        )}
        {!showInput && showSlider && (
          <span className="text-sm font-medium shrink-0">{numValue}</span>
        )}
        {unit && <span className="text-sm text-gray-500 shrink-0">{unit}</span>}
      </div>
      {showSlider && hasRange && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 w-12 text-right shrink-0">{min}</span>
          <SingleSlider value={numValue} min={min!} max={max!} step={s}
            onChange={(v) => onChange(v)} disabled={disabled} />
          <span className="text-xs text-gray-500 w-12 shrink-0">{max}</span>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// RangeInput — dual-thumb range slider + number inputs
// ============================================================================

function RangeInput({ value, onChange, onTab, min, max, step, unit, inputMode, disabled }: {
  value: any;
  onChange: (val: {min: number; max: number}) => void;
  onTab: () => void;
  min: number;
  max: number;
  step?: number | null;
  unit?: string | null;
  inputMode?: string | null;
  disabled: boolean;
}) {
  const rangeMin = value?.min ?? min;
  const rangeMax = value?.max ?? max;
  const s = step ?? (max - min) / 100;
  const u = unit ?? '';

  const clampMin = (v: number) => Math.max(min, Math.min(v, rangeMax));
  const clampMax = (v: number) => Math.min(max, Math.max(v, rangeMin));

  // Percentage positions for the colored range bar
  const minPct = ((rangeMin - min) / (max - min)) * 100;
  const maxPct = ((rangeMax - min) / (max - min)) * 100;

  const maxInputRef = useRef<HTMLDivElement>(null);

  return (
    <div className="max-w-lg space-y-1">
      {/* Number inputs on top — same size as numeric input, validate on blur */}
      <div className="flex items-center gap-2">
        <ClampedNumberInput value={rangeMin}
          onChange={(v) => onChange({ min: clampMin(v), max: rangeMax })}
          min={min} max={rangeMax} step={s} disabled={disabled}
          className="w-20 px-2 py-0.5 text-sm border border-gray-300 rounded bg-white text-center" />
        <span className="text-xs text-gray-400">to</span>
        <div ref={maxInputRef} onKeyDown={(e) => {
          if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); onTab(); }
        }}>
          <ClampedNumberInput value={rangeMax}
            onChange={(v) => onChange({ min: rangeMin, max: clampMax(v) })}
            min={rangeMin} max={max} step={s} disabled={disabled}
            className="w-20 px-2 py-0.5 text-sm border border-gray-300 rounded bg-white text-center" />
        </div>
        {u && <span className="text-sm text-gray-500">{u}</span>}
      </div>
      {/* Slider row: min label — dual thumb — max label */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500 w-12 text-right shrink-0">{min}</span>
        <div className="relative flex-1 h-6 flex items-center">
          <div className="absolute left-0 right-0 h-1 bg-gray-200 rounded" />
          <div className="absolute h-1 bg-blue-400 rounded"
            style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }} />
          <input type="range" tabIndex={-1}
            className={`absolute left-0 right-0 appearance-none bg-transparent pointer-events-none
              [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto ${SLIDER_THUMB}`}
            min={min} max={max} step={s} value={rangeMin}
            onChange={(e) => onChange({ min: clampMin(parseFloat(e.target.value)), max: rangeMax })}
            disabled={disabled}
            style={{ zIndex: rangeMin > max - (max - min) * 0.1 ? 5 : 3 }} />
          <input type="range" tabIndex={-1}
            className={`absolute left-0 right-0 appearance-none bg-transparent pointer-events-none
              [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto ${SLIDER_THUMB}`}
            min={min} max={max} step={s} value={rangeMax}
            onChange={(e) => onChange({ min: rangeMin, max: clampMax(parseFloat(e.target.value)) })}
            disabled={disabled}
            style={{ zIndex: 4 }} />
        </div>
        <span className="text-xs text-gray-500 w-12 shrink-0">{max}</span>
      </div>
    </div>
  );
}
